import { useCallback } from "react";

import { Box, Collapse, IconButton } from "@mui/material";
import Stack from "@mui/material/Stack";
import { ChevronDownIcon, ChevronUpIcon, Typography } from "components";
import { error, gray } from "configs/colors";
import { useToggle } from "hooks";
import { LoadingOverlay, TLNStack } from "pages/Components";
import i18n from "translations/i18n";
import { rem } from "utils";
import { getPrimaryColor } from "utils/theme";

interface BoxInfoProps {
  title?: string;
  titleI18n?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  action?: React.ReactNode;
  maxWidth?: number;
  defaultCollapsed?: boolean;
  loading?: boolean;
  required?: boolean;
  noBorder?: boolean;
}

const BoxInfo: React.FC<BoxInfoProps> = ({
  title,
  icon,
  titleI18n,
  children,
  maxWidth,
  defaultCollapsed = true,
  loading = false,
  required = false,
  noBorder = false,
}) => {
  const [collapsed, toggleCollapsed] = useToggle(defaultCollapsed);
  const handleCollapse = useCallback(() => {
    toggleCollapsed();
  }, [toggleCollapsed]);
  return (
    <LoadingOverlay loading={loading}>
      <Stack
        borderRadius={noBorder ? "0px" : "12px"}
        border={`1px solid ${gray[200]}`}
      >
        <TLNStack.SpaceBetween
          padding={`${rem(8)} ${rem(24)}`}
          bgcolor={gray[150]}
          sx={
            !noBorder
              ? {
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                  borderBottomLeftRadius: collapsed ? "0px" : "12px",
                  borderBottomRightRadius: collapsed ? "0px" : "12px",
                }
              : {}
          }
          onClick={handleCollapse}
        >
          <Stack
            direction="row"
            columnGap={rem(12)}
            alignItems="center"
            sx={{
              "& svg path": {
                color: getPrimaryColor()[700],
              },
            }}
          >
            {icon}
            <Typography
              fontWeight={600}
              type="TextLG"
              color="grey.900"
              sx={{
                "&::after": {
                  content: required ? '"*"' : '""',
                  color: error[600],
                  marginLeft: rem(4),
                },
              }}
            >
              {titleI18n ? i18n.t(titleI18n) : title}
            </Typography>
          </Stack>
          <IconButton size="medium">
            {collapsed ? (
              <ChevronUpIcon fontSize="medium" sx={{ color: gray[500] }} />
            ) : (
              <ChevronDownIcon fontSize="medium" sx={{ color: gray[500] }} />
            )}
          </IconButton>
        </TLNStack.SpaceBetween>
        <Collapse in={collapsed}>
          <Stack
            borderTop={`1px solid ${gray[200]}`}
            padding={`${rem(24)} ${rem(32)}`}
            sx={
              maxWidth
                ? {
                    [`@media screen and (max-width: ${maxWidth}px)`]: {
                      overflowX: "scroll",
                    },
                  }
                : {}
            }
          >
            <Box
              sx={
                maxWidth
                  ? {
                      [`@media screen and (max-width: ${maxWidth}px)`]: {
                        width: `${maxWidth}px`,
                      },
                    }
                  : {}
              }
            >
              {children}
            </Box>
          </Stack>
        </Collapse>
      </Stack>
    </LoadingOverlay>
  );
};

export default BoxInfo;
